/**
 * SEARCH-002 — Sous-hook « création + aucun résultat + clavier » de la palette (⌘K).
 *
 * Extrait mécaniquement de `SearchPalette.tsx` / `useSearchPalette` (aucun
 * changement de logique) : options de création contextuelle, actions de l'état
 * « aucun résultat », présélections (résultats / aucun résultat) et handlers
 * clavier (navigation 2D, ⌘↵, Backspace scope). Appelé INCONDITIONNELLEMENT par
 * `useSearchPalette` à sa position d'origine (ordre des hooks préservé).
 */

import { type KeyboardEvent, useCallback, useEffect, useMemo } from 'react'
import { Plus, Sparkles } from 'lucide-react'
import { type SearchHit, type SourceType } from '@/lib/searchCategories'
import {
    ARROW_DIR,
    CREATABLE,
    type CreatableType,
    type NoResultAction,
    nextSpatialValue,
} from './searchPaletteHelpers'

export interface UseSearchPaletteActionsArgs {
    hits: SearchHit[]
    bestHit: SearchHit | null
    input: string
    hasQuery: boolean
    isQuestion: boolean
    scopeType: SourceType | null
    showNoResults: boolean
    selectedValue: string
    noResultSel: string
    setSelectedValue: (value: string) => void
    setNoResultSel: (updater: string | ((prev: string) => string)) => void
    setScopeType: (type: SourceType | null) => void
    hasPermission: (slug: string) => boolean
    go: (route: string) => void
    rememberQuery: () => void
    handleHelp: () => void
}

export function useSearchPaletteActions({
    hits,
    bestHit,
    input,
    hasQuery,
    isQuestion,
    scopeType,
    showNoResults,
    selectedValue,
    noResultSel,
    setSelectedValue,
    setNoResultSel,
    setScopeType,
    hasPermission,
    go,
    rememberQuery,
    handleHelp,
}: UseSearchPaletteActionsArgs) {
    // Création contextuelle (§5) : par type ayant des résultats (ordre = nb décroissant),
    // plafonnée à 3 ; à scope actif → le seul type filtré ; sans résultat → éventail défaut.
    const countByType = useMemo(() => {
        const m = new Map<SourceType, number>()
        hits.forEach((h) => m.set(h.type, (m.get(h.type) ?? 0) + 1))
        return m
    }, [hits])
    const createOptions = useMemo<CreatableType[]>(() => {
        if (!hasQuery || isQuestion) return []
        const allowed = CREATABLE.filter((c) => hasPermission(c.permission))
        if (allowed.length === 0) return []
        if (scopeType) {
            const c = allowed.find((c) => c.type === scopeType)
            return c ? [c] : []
        }
        const withResults = allowed
            .filter((c) => (countByType.get(c.type) ?? 0) > 0)
            .sort((a, b) => (countByType.get(b.type) ?? 0) - (countByType.get(a.type) ?? 0))
        return (withResults.length > 0 ? withResults : allowed).slice(0, 3)
    }, [hasQuery, isQuestion, scopeType, countByType, hasPermission])

    // Création « primaire » = 1er type proposé (le plus pertinent). Cible du
    // raccourci ⌘↵ et de l'action mise en avant à l'état « aucun résultat ».
    const primaryCreate = createOptions[0] ?? null
    const launchPrimaryCreate = useCallback(() => {
        if (!primaryCreate) return
        rememberQuery()
        // Paramètre DÉDIÉ `create_q` (≠ `q` du filtre de table) : ouvre la modale
        // de création et pré-remplit le nom, sans filtrer la table cible.
        go(`${primaryCreate.route}&create_q=${encodeURIComponent(input.trim())}`)
    }, [primaryCreate, go, input, rememberQuery])

    // Actions navigables de l'état « aucun résultat » : créations (sauf si la saisie
    // est une question) → assistant (si question + assistant actif) → aide. Naviguées
    // aux flèches (bague), validées à l'Entrée sur l'action surlignée.
    const noResultActions = useMemo<NoResultAction[]>(() => {
        if (!showNoResults) return []
        const list: NoResultAction[] = []
        if (!isQuestion) {
            for (const c of createOptions) {
                list.push({
                    value: `nr:create:${c.type}`,
                    kind: 'create',
                    // Noir (primaire) seulement si la création est NON AMBIGUË (1 seule option).
                    variant: createOptions.length === 1 ? 'primary' : 'default',
                    icon: Plus,
                    create: c,
                    run: () => {
                        rememberQuery()
                        go(`${c.route}&create_q=${encodeURIComponent(input.trim())}`)
                    },
                })
            }
        }
        // Aide UNIFIÉE (violet, étincelle) — un seul canal, plus d'assistant séparé (§4).
        list.push({ value: 'nr:help', kind: 'help', variant: 'ai', icon: Sparkles, run: handleHelp })
        return list
    }, [showNoResults, isQuestion, createOptions, input, go, rememberQuery, handleHelp])

    // Présélection des RÉSULTATS (jamais l'ancienne position) : meilleur résultat / 1ʳᵉ ligne.
    useEffect(() => {
        const top = bestHit ?? hits[0] ?? null
        setSelectedValue(top ? `${top.type}:${top.id}` : '')
    }, [hits, bestHit, setSelectedValue])

    // Présélection des actions « AUCUN RÉSULTAT » (état dédié) : 1ʳᵉ action navigable
    // (Entrée prête sans flèche préalable), en gardant la sélection courante si valide.
    useEffect(() => {
        if (!showNoResults) return
        setNoResultSel((prev) =>
            noResultActions.some((a) => a.value === prev) ? prev : (noResultActions[0]?.value ?? ''),
        )
    }, [showNoResults, noResultActions, setNoResultSel])

    const handleCommandKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key !== 'Enter' || !showNoResults) return
            // Les actions « aucun résultat » sont des boutons (pas des CommandItem) :
            // cmdk ne gère pas leur Entrée → on déclenche l'action SURLIGNÉE nous-mêmes
            // (celle pointée par `noResultSel`, sinon la 1ʳᵉ par défaut).
            e.preventDefault()
            const action = noResultActions.find((a) => a.value === noResultSel) ?? noResultActions[0]
            action?.run()
        },
        [showNoResults, noResultActions, noResultSel],
    )
    const handleInputKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Backspace' && input === '' && scopeType) {
                e.preventDefault()
                setScopeType(null)
                return
            }
            // ⌘/Ctrl + ↵ : lance directement la création contextuelle primaire
            // (modale + pré-remplissage) sans quitter le clavier ni viser la souris.
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && primaryCreate) {
                e.preventDefault()
                e.stopPropagation()
                launchPrimaryCreate()
                return
            }
            // Navigation 2D « quadrillage » (voisin géométrique le plus proche) sur
            // les éléments `[data-nav]` — dans DEUX états :
            //   • champ VIDE → état vide (2 colonnes + pastilles + récents) ;
            //   • AUCUN RÉSULTAT → les propositions (créations / assistant / aide).
            // Ailleurs (liste de résultats), on laisse cmdk gérer ↑ ↓ ; ← / → = curseur.
            if ((input === '' || showNoResults) && ARROW_DIR[e.key]) {
                const root = (e.currentTarget as HTMLElement).closest('[cmdk-root]')
                if (root) {
                    // « Aucun résultat » → sélection dédiée (`noResultSel`) ; état vide → `selectedValue`.
                    const current = showNoResults ? noResultSel : selectedValue
                    const next = nextSpatialValue(root, ARROW_DIR[e.key], current)
                    if (next) {
                        e.preventDefault()
                        e.stopPropagation() // empêche cmdk de re-déplacer en parallèle
                        if (showNoResults) setNoResultSel(next)
                        else setSelectedValue(next)
                    }
                }
            }
        },
        [
            input,
            scopeType,
            selectedValue,
            noResultSel,
            showNoResults,
            primaryCreate,
            launchPrimaryCreate,
            setNoResultSel,
            setScopeType,
            setSelectedValue,
        ],
    )

    return {
        createOptions,
        primaryCreate,
        launchPrimaryCreate,
        noResultActions,
        handleCommandKeyDown,
        handleInputKeyDown,
    }
}
